import fetcher, { setAccountToHeader } from 'api/fetcher';
import {
    addSearchParamsToUrl,
    checkStorageAccountId,
    redirectIfReadOnly,
    resolvePromisesMap,
    setCurrentAccountIdToStorages,
} from 'utils/clickme';
import { readAndClearTargetAccountId, shouldSwitchToTargetAccount } from 'utils/clickme/targetAccountDeeplink';
import { CLICKME_LINK_PROMO } from 'utils/constants/app';
import { Methods } from 'utils/constants/methods';
import { CURRENT_ACCOUNT_ID_STORAGE } from 'utils/constants/user';
import type { FetchCacheController } from 'utils/fetchCache';

import { localStorageWrapper, sessionStorageWrapper } from '@hh.ru/browser-api-utils';
import { RProgressApi } from '@hh.ru/rprogress';

import { ROUTES_REQUIREMENTS } from 'app/routes';
import { RouteType } from 'app/routes/routeType';

import {
    selectFetchCacheSettings,
    setCheckedStorageAccountId,
    setFetchCacheSettings,
    setLoadPage,
} from 'store/autoReducers';
import type { Action } from 'store/createReducer';
import type { GetState, RequestsMap, UserResponse } from 'store/middlewares/fetchRouter/types';
import { FetchAction } from 'store/middlewares/fetchRouter/types';
import {
    cacheResponse,
    fetchUnloaded,
    getRequestsMap,
    getRoute,
    injectData,
    needLoadMore,
    onError,
} from 'store/middlewares/fetchRouter/utils';
import { setUser } from 'store/models/user';
import { AppDispatch } from 'store/utils';

const fetchPageData = (
    fetchCacheController: FetchCacheController,
    action: FetchAction,
    url: string,
    next: AppDispatch,
    dispatch: AppDispatch,
    getState: GetState,
    urlWasChanged = false
): Promise<void> => {
    RProgressApi.start();
    dispatch(setLoadPage(true));

    const route = getRoute(url);

    if (route) {
        const { methods, resolve, match, type } = route;
        const state = getState();
        dispatch(redirectIfReadOnly(route.path, state.user.user?.currentAccount.status));
        const cacheSettings = selectFetchCacheSettings(state);
        const historyState = action.payload?.args?.[1];

        fetchCacheController.invalidateCache(cacheSettings.fieldsToInvalidate);
        cacheSettings.fieldsToInvalidate.length = 0;

        const topLevelActions = [setFetchCacheSettings(cacheSettings)];

        const fetchAllRequests = (): Promise<void> => {
            const additionalActions = [] as Action[];
            const state = getState();
            const routeMethods = ROUTES_REQUIREMENTS[type].methods({ state });
            const pageMethods = methods ? methods({ match, state, historyState }) : null;
            const requestSettings = { routeMethods, ...(pageMethods && { pageMethods }) };

            return resolvePromisesMap(getRequestsMap(requestSettings, fetchCacheController) as RequestsMap)
                .then(({ pageMethods = {}, routeMethods }) => {
                    // additionalMethods check just for typescript, because main check in needLoadMore
                    if (route.additionalMethods && needLoadMore({ ...route, state, result: pageMethods })) {
                        return fetchUnloaded({
                            ...route,
                            additionalMethods: route.additionalMethods,
                            result: pageMethods,
                            state,
                            fetchCacheController,
                            requestSettings,
                        }).then((additionalPageMethodsResponses) => ({
                            pageMethods: { ...pageMethods, ...additionalPageMethodsResponses },
                            routeMethods,
                        }));
                    }

                    return { pageMethods, routeMethods };
                })
                .then((fetchData) => {
                    RProgressApi.complete();

                    cacheResponse(requestSettings, fetchData, fetchCacheController);

                    const pageActions = resolve
                        ? resolve({ result: fetchData.pageMethods, match, state, historyState })
                        : [];
                    const routeActions = ROUTES_REQUIREMENTS[type].resolve({ result: fetchData.routeMethods, state });
                    const actions = [...pageActions, ...routeActions, ...additionalActions, ...topLevelActions];
                    injectData(actions, urlWasChanged, action, next);
                })
                .catch((err) => onError(type, urlWasChanged, action, next, err));
        };

        if (type === RouteType.UserRoute && !state.checkedStorageAccountId) {
            const itemAccountIdFromLocal = localStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
            const itemAccountIdFromSession = sessionStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
            if (itemAccountIdFromSession) {
                setAccountToHeader(itemAccountIdFromSession);
            } else if (itemAccountIdFromLocal) {
                setAccountToHeader(itemAccountIdFromLocal);
            }

            const applyUserDataAndContinue = (data: UserResponse): Promise<void> => {
                const { accounts, currentAccount } = data.user || {};
                const currentAccountId = currentAccount ? String(currentAccount.id) : undefined;

                checkStorageAccountId(accounts || [], currentAccountId);
                dispatch(setUser(data?.user || null));
                dispatch(setCheckedStorageAccountId(true));
                dispatch(redirectIfReadOnly(route.path, data.user?.currentAccount.status));

                return fetchAllRequests();
            };

            const loadUserData = (): Promise<UserResponse> =>
                fetcher.get(Methods.User).then((userData: UserResponse) => {
                    fetchCacheController.setCache(Methods.User, userData);
                    return userData;
                });

            return loadUserData()
                .then((data: UserResponse) => {
                    const { accounts, currentAccount } = data.user || {};
                    const currentAccountId = currentAccount ? String(currentAccount.id) : undefined;
                    const validatedTargetAccountId = readAndClearTargetAccountId();

                    if (validatedTargetAccountId !== null) {
                        const validatedTargetAccountIdAsString = String(validatedTargetAccountId);
                        const shouldSwitchAccount = shouldSwitchToTargetAccount(
                            accounts || [],
                            validatedTargetAccountIdAsString,
                            currentAccountId
                        );

                        if (!shouldSwitchAccount) {
                            return applyUserDataAndContinue(data);
                        }

                        const previousAccountId = currentAccountId || '';

                        setCurrentAccountIdToStorages(validatedTargetAccountIdAsString);
                        setAccountToHeader(validatedTargetAccountIdAsString);

                        return loadUserData()
                            .then((switchedUserData: UserResponse) => {
                                return applyUserDataAndContinue(switchedUserData);
                            })
                            .catch(() => {
                                setCurrentAccountIdToStorages(previousAccountId);
                                setAccountToHeader(previousAccountId);

                                return applyUserDataAndContinue(data);
                            });
                    }

                    return applyUserDataAndContinue(data);
                })
                .catch(() => {
                    setCurrentAccountIdToStorages('');
                    window.location.assign(addSearchParamsToUrl(CLICKME_LINK_PROMO));
                });
        }

        return fetchAllRequests();
    }

    return Promise.resolve();
};

export default fetchPageData;
